import { useContext } from 'react'
import { Dices } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import { updateBonusRollTarget } from 'app/frontend/base/api/character'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { LootContext } from '../context'

type Props = {
  characterId: number
  disabled?: boolean
}

const BonusRollTarget = ({ characterId, disabled }: Props) => {
  const { team } = useTeam()
  const { selectedSeason } = useContext(LootContext)
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const teamQueryKey = ['team', { path: team.path }]

  const encounters = selectedSeason.instances.flatMap(
    (instance) => instance.encounters,
  )

  const selectedIds =
    team.members?.find((member) => member.characterReference.id === characterId)
      ?.characterReference.bonusRollEncounterIds ?? []

  const mutation = useMutation({
    mutationFn: (nextIds: number[]) =>
      updateBonusRollTarget(team.id, characterId, nextIds),
    onMutate: async (nextIds) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((member) =>
                member.characterReference.id === characterId
                  ? {
                      ...member,
                      characterReference: {
                        ...member.characterReference,
                        bonusRollEncounterIds: nextIds,
                      },
                    }
                  : member,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _nextIds, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const toggleEncounter = (encounterId: number) => {
    if (disabled || mutation.isPending) return

    mutation.mutate(
      selectedIds.includes(encounterId)
        ? selectedIds.filter((id) => id !== encounterId)
        : [...selectedIds, encounterId],
    )
  }

  if (!selectedSeason.bonus_rolls_available || encounters.length === 0)
    return null

  return (
    <div>
      <h4 className='flex items-center gap-2 mb-1'>
        <Dices size={20} className='text-info-foreground' />
        Bonus roll targets
      </h4>
      <div className='grid gap-2 grid-cols-[repeat(auto-fit,minmax(5rem,1fr))]'>
        {encounters.map((encounter) => {
          const active = selectedIds.includes(encounter.id)

          return (
            <button
              key={encounter.id}
              type='button'
              disabled={disabled || mutation.isPending}
              onClick={() => toggleEncounter(encounter.id)}
              className={`hover:bg-accent min-w-0 flex flex-col items-center gap-1 px-2 pt-2 pb-1.5 rounded-md border transition-colors disabled:cursor-not-allowed group ${
                active ? 'border-success-foreground bg-accent' : 'bg-card'
              }`}
            >
              <img
                src={encounter.thumbnailUrl}
                className={`w-10 h-10 rounded-full object-cover shrink-0 transition-all ${
                  active ? '' : 'not-group-hover:grayscale-100'
                }`}
                alt={encounter.name}
              />
              <span
                className={`text-sm text-center truncate w-full font-normal ${
                  active ? 'text-success-foreground' : ''
                }`}
              >
                {encounter.name}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BonusRollTarget
